/**
 * Live delivery-cutoff countdown, driven off UK wall-clock time
 * (Europe/London, so BST/GMT are handled automatically by the browser's
 * Intl implementation -- not the visitor's own timezone).
 *
 * Renders into every element carrying [data-delivery-countdown], so the
 * same script/interval can drive multiple instances on one page (e.g.
 * announcement bar + product page, both present on a product page view)
 * without duplicating logic. Safe to include more than once: the guard
 * below no-ops any run after the first.
 *
 * Cutoff (3pm) applies Monday-Friday. Ordering before cutoff on a
 * business day ships that same day; after cutoff (or on a non-dispatch
 * day) it ships the next business day and arrives the delivery day after
 * that. DPD does not deliver on Saturdays for this store (confirmed), so
 * that second step skips straight to Monday when it lands on a weekend.
 * If Saturday delivery is ever enabled, the only edit needed is
 * DPD_SATURDAY_DELIVERY below.
 *
 * The warehouse (TropShip) doesn't process or dispatch over the weekend,
 * so Friday-after-cutoff through Sunday all dispatch Monday and arrive
 * the next delivery day after that -- Tuesday, same dispatch-day-plus-one
 * rule as any other day.
 */
(function () {
  if (window.__rpDeliveryCountdownInitialized) return;
  window.__rpDeliveryCountdownInitialized = true;

  var DPD_SATURDAY_DELIVERY = false;
  var CUTOFF_HOUR = 15;
  var CUTOFF_MINUTE = 0;
  var URGENT_THRESHOLD_SECONDS = 15 * 60;

  var DAY_ORDER = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  var BUSINESS_DAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri']; // warehouse dispatch days
  var FULL_WEEKDAY_NAME = {
    Sun: 'Sunday',
    Mon: 'Monday',
    Tue: 'Tuesday',
    Wed: 'Wednesday',
    Thu: 'Thursday',
    Fri: 'Friday',
    Sat: 'Saturday',
  };

  function isBusinessDay(name) {
    return BUSINESS_DAYS.indexOf(name) !== -1;
  }

  function isDeliveryDay(name) {
    if (name === 'Sat') return DPD_SATURDAY_DELIVERY;
    return isBusinessDay(name);
  }

  function nextDayName(name) {
    return DAY_ORDER[(DAY_ORDER.indexOf(name) + 1) % 7];
  }

  // Next warehouse dispatch day after `name` (always skips Sat/Sun).
  function nextBusinessDayName(name) {
    var next = nextDayName(name);
    while (!isBusinessDay(next)) next = nextDayName(next);
    return next;
  }

  // Next day a dispatched parcel can actually land after `name` (skips
  // Sun always, and Sat unless DPD_SATURDAY_DELIVERY is enabled).
  function nextDeliveryDayName(name) {
    var next = nextDayName(name);
    while (!isDeliveryDay(next)) next = nextDayName(next);
    return next;
  }

  var ukTimeFormatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Europe/London',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
    weekday: 'short',
  });

  function getUkNow() {
    var parts = ukTimeFormatter.formatToParts(new Date());
    var map = {};
    parts.forEach(function (part) {
      map[part.type] = part.value;
    });
    // Some engines report midnight as "24" under hour12: false.
    var hour = map.hour === '24' ? 0 : parseInt(map.hour, 10);
    return {
      weekday: map.weekday, // 'Mon' | 'Tue' | 'Wed' | 'Thu' | 'Fri' | 'Sat' | 'Sun'
      hour: hour,
      minute: parseInt(map.minute, 10),
      second: parseInt(map.second, 10),
    };
  }

  function formatRemaining(totalSeconds) {
    if (totalSeconds >= 3600) {
      var h = Math.floor(totalSeconds / 3600);
      var m = Math.floor((totalSeconds % 3600) / 60);
      return h + 'h ' + m + 'm';
    }
    if (totalSeconds >= 60) {
      return Math.floor(totalSeconds / 60) + 'm';
    }
    return Math.max(totalSeconds, 0) + 's';
  }

  function computeMessage() {
    var now = getUkNow();
    var secondsSinceMidnight = now.hour * 3600 + now.minute * 60 + now.second;
    var cutoffSeconds = CUTOFF_HOUR * 3600 + CUTOFF_MINUTE * 60;
    var beforeCutoff = secondsSinceMidnight < cutoffSeconds;

    if (isBusinessDay(now.weekday) && beforeCutoff) {
      // Ships today, arrives the next day a parcel can land. The
      // remaining-time figure is wrapped in its own span (styled as a
      // badge in CSS) so it reads as the focal point of the message --
      // safe to build as HTML since every piece is either a fixed string
      // or a number/weekday name computed above, never user input.
      var remaining = cutoffSeconds - secondsSinceMidnight;
      var arrival = FULL_WEEKDAY_NAME[nextDeliveryDayName(now.weekday)];
      return {
        html:
          'Order within <span class="delivery-countdown__time">' +
          formatRemaining(remaining) +
          '</span> for delivery ' +
          arrival,
        urgent: remaining <= URGENT_THRESHOLD_SECONDS,
      };
    }

    // Cutoff has passed today, or today isn't a dispatch day (Sat/Sun) --
    // ships on the next business day, arrives the delivery day after that.
    var dispatchDay = nextBusinessDayName(now.weekday);
    var arrivalDay = FULL_WEEKDAY_NAME[nextDeliveryDayName(dispatchDay)];
    return { html: 'Order now for delivery ' + arrivalDay, urgent: false };
  }

  function render() {
    var message = computeMessage();
    var targets = document.querySelectorAll('[data-delivery-countdown]');
    for (var i = 0; i < targets.length; i++) {
      targets[i].innerHTML = message.html;
      var container = targets[i].closest('.delivery-countdown');
      if (container) {
        container.classList.toggle('delivery-countdown--urgent', message.urgent);
      }
    }
  }

  render();
  setInterval(render, 1000);
})();
